import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';
import CreditReceiveModal from './CreditReceiveModal';
import CreditChangeModal from './CreditChangeModal';
import { ExcelModalButton } from '../../components/ExcelExportModal';
import CreditReportModal from './CreditReportModal';

const toFarsi = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng   = s => String(s||'').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt  = n => (Number(n||0) === 0) ? '•' : Number(n).toLocaleString('fa-IR');

// کدهای نوع منبع، منبع «سایر» و نوع اعتبار (اندیس = مقدار ذخیره‌شده)
const SOURCE_TYPES  = ['', 'عمومی', 'اختصاصی', 'سایر'];
const OTHER_SOURCES = ['', 'هدایا و کمک های بلاعوض', 'تنخواه گردان دریافتی از سایر واحدها', 'درآمد خانه های سازمانی', 'سایر'];
const CREDIT_TYPES  = ['', 'مصوب', 'ابلاغی'];
const ACC_KINDS     = ['اعتبارات هزینه‌ای', 'اعتبارات تملک دارایی‌های سرمایه‌ای'];
const REGION_TYPES  = ['استانی', 'ملی', ''];

const EMPTY_FORM = {
  code:0, title:'', brief_title:'', source_type:1, other_source_type:0,
  credit_type:1, accounting_kind:0, region_type:0, depends_year:false,
  cost_center:false, program:false, project:false, hazineh:false,
  exception_pct:0, account_code:0, account_display:'',
};

const API = (path, opts={}) => fetch(`/api/accounting${path}`, {
  headers: { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

// ستون‌های گرید — بسته به تیک حسابداری/بودجه پویا ساخته می‌شود
function buildColumns(showAcc, showBudget) {
  const C = [
    { key:'code',  label:'کد',            w:70,  bg:'#1a2f50', tc:'#fff', mono:true,  render:r=>r.code },
    { key:'title', label:'عنوان',         w:240, bg:'#1a2f50', tc:'#fff', right:true, render:r=>r.title },
    { key:'brief', label:'عنوان اختصاری', w:150, bg:'#1a2f50', tc:'#fff', right:true, render:r=>r.brief_title },
  ];
  if (showBudget) {
    C.push(
      { key:'bcredit',   label:'اعتبار',                   w:120, bg:'#1b5e20', tc:'#fff', num:true, val:r=>r.budget_credit },
      { key:'ballocate', label:'تخصیص',                    w:120, bg:'#1b5e20', tc:'#fff', num:true, val:r=>r.budget_allocate },
      { key:'rcs',       label:'دریافتی نقد منبع',         w:120, bg:'#00695c', tc:'#fff', num:true, val:r=>r.recv_cash_source },
      { key:'rds',       label:'دریافتی اسناد منبع',       w:120, bg:'#00695c', tc:'#fff', num:true, val:r=>r.recv_doc_source },
      { key:'rfs',       label:'دریافتی تنخواه منبع',      w:120, bg:'#00695c', tc:'#fff', num:true, val:r=>r.recv_fund_source },
      { key:'rcp',       label:'دریافتی نقد برنامه/طرح',   w:130, bg:'#00838f', tc:'#fff', num:true, val:r=>r.recv_cash_program },
      { key:'rdp',       label:'دریافتی اسناد برنامه/طرح', w:130, bg:'#00838f', tc:'#fff', num:true, val:r=>r.recv_doc_program },
      { key:'rfp',       label:'دریافتی تنخواه برنامه/طرح',w:130, bg:'#00838f', tc:'#fff', num:true, val:r=>r.recv_fund_program },
    );
  }
  if (showAcc) {
    C.push(
      { key:'db', label:'بدهکار(قبل)',   w:110, bg:'#0d2137', tc:'#aed6f1', num:true, val:r=>r.debit_before },
      { key:'cb', label:'بستانکار(قبل)', w:110, bg:'#0d2137', tc:'#aed6f1', num:true, val:r=>r.credit_before },
      { key:'dc', label:'بدهکار(جاری)',  w:110, bg:'#0d2137', tc:'#C8A84B', num:true, val:r=>r.debit_current },
      { key:'cc', label:'بستانکار(جاری)',w:110, bg:'#0d2137', tc:'#C8A84B', num:true, val:r=>r.credit_current },
      { key:'dt', label:'بدهکار(جمع)',   w:110, bg:'#0a1628', tc:'#aed6f1', num:true, val:r=>r.debit_total },
      { key:'ct', label:'بستانکار(جمع)', w:110, bg:'#0a1628', tc:'#aed6f1', num:true, val:r=>r.credit_total },
      { key:'rd', label:'مانده بدهکار',  w:110, bg:'#1b5e20', tc:'#fff', num:true, val:r=>r.remain_debit,  strong:'#1b5e20' },
      { key:'rc', label:'مانده بستانکار',w:110, bg:'#b71c1c', tc:'#fff', num:true, val:r=>r.remain_credit, strong:'#b71c1c' },
    );
  }
  C.push(
    { key:'src',    label:'نوع منبع',      w:85,  bg:'#1a2f50', tc:'#fff', render:r=>r.source_type_name },
    { key:'akind',  label:'نوع حساب',      w:120, bg:'#1a2f50', tc:'#fff', render:r=>r.accounting_kind_name },
    { key:'region', label:'استانی/ملی',    w:70,  bg:'#1a2f50', tc:'#fff', render:r=>r.region_type_name },
    { key:'ccn',    label:'مرکز هزینه',    w:60,  bg:'#1a2f50', tc:'#fff', check:r=>r.cost_center },
    { key:'progn',  label:'طرح/برنامه',    w:60,  bg:'#1a2f50', tc:'#fff', check:r=>r.program },
    { key:'projn',  label:'پروژه',         w:50,  bg:'#1a2f50', tc:'#fff', check:r=>r.project },
    { key:'hazn',   label:'کد هزینه',      w:55,  bg:'#1a2f50', tc:'#fff', check:r=>r.hazineh },
    { key:'exc',    label:'درصد استثناء',  w:70,  bg:'#1a2f50', tc:'#fff', num:true, val:r=>r.exception_pct },
    { key:'acc',    label:'شماره حساب',    w:170, bg:'#1a2f50', tc:'#fff', right:true, render:r=>r.account_display },
  );
  return C;
}

const FInput = ({ label, name, value, onChange, type='text', half }) => (
  <div style={{display:'flex', flexDirection:'column', gap:'4px', flex: half ? '0 0 48%' : '1'}}>
    <label style={ui.mlabel}>{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} style={ui.input} />
  </div>
);
const FSelect = ({ label, name, value, onChange, opts }) => (
  <div style={{display:'flex', flexDirection:'column', gap:'4px', flex:1}}>
    <label style={ui.mlabel}>{label}</label>
    <select name={name} value={value} onChange={onChange} style={ui.input}>
      {opts.map((o,i) => <option key={i} value={i}>{o || '— انتخاب کنید —'}</option>)}
    </select>
  </div>
);
const FCheck = ({ label, name, checked, onChange }) => (
  <label style={{display:'flex', alignItems:'center', fontSize:'12px', color:'#444', cursor:'pointer', padding:'4px 8px', background:'#fff', borderRadius:'6px', border:'1px solid #e0e0e0'}}>
    <input type="checkbox" name={name} checked={checked} onChange={onChange} style={{marginLeft:'6px'}} />
    {label}
  </label>
);

export default function AccountingCredit({ user, onBack, vbForm }) {
  // دکمه‌های فرم FRMCREDIT؛ دیدن = دسترسی
  const vbf = useFormButtons(vbForm || 'FRMCREDIT', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK","BTNCREDITRECEIVE","BTNCHANGE"]);
  const year = new Date().toLocaleDateString('fa-IR').split('/')[0]
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());

  const [data,     setData]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [dateInfo, setDateInfo] = useState({from:'', to:''});
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');
  const [total,    setTotal]    = useState(0);

  const [showAcc,    setShowAcc]    = useState(true);
  const [showBudget, setShowBudget] = useState(false);
  const [showAll,    setShowAll]    = useState(false);
  const [by,         setBy]         = useState('date');
  const [rangeFrom,  setRangeFrom]  = useState('');
  const [rangeTo,    setRangeTo]    = useState('');
  const [accLookup, setAccLookup] = useState([]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const columns = buildColumns(showAcc, showBudget);
  const [colW, setColW] = useState({});
  const colWRef = useRef({});
  useEffect(()=>{ colWRef.current = colW; }, [colW]);
  const wOf = c => colW[c.key] ?? c.w;
  const startResize = useCallback((key, base, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX;
    const onMove = ev => { const nw = Math.max(40, base + (sx - ev.clientX)); setColW(w => ({ ...w, [key]: nw })); };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const p = new URLSearchParams({
      year, search, by, from: toEng(rangeFrom), to: toEng(rangeTo),
      accounting: showAcc ? 1 : 0, budget: showBudget ? 1 : 0, all: showAll ? 1 : 0,
    });
    const j = await API(`/credits/?${p.toString()}`);
    setLoading(false);
    if (j.ok) { setData(j.data); setTotal(j.total || j.data.length); setDateInfo({ from: j.date_from, to: j.date_to }); }
    else setError(j.msg);
  }, [year, search, by, rangeFrom, rangeTo, showAcc, showBudget, showAll]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, showAcc, showBudget, showAll, by]);

  const filtered = data.filter(r =>
    !search || (r.title||'').toLowerCase().includes(search.toLowerCase()) || String(r.code).includes(search));

  // ستون‌ها و ردیف‌های مسطح برای ExcelExportModal
  const excelCols = React.useMemo(() => columns.map(c => ({ key: c.key, label: c.label, num: !!c.num })), [columns]);
  const excelRows = React.useMemo(() => filtered.map(r => {
    const flat = {};
    columns.forEach(c => {
      if (c.num && c.val) flat[c.key] = c.val(r);
      else if (c.check) flat[c.key] = c.check(r) ? '✓' : '';
      else if (c.render) flat[c.key] = c.render(r);
      else flat[c.key] = r[c.key] ?? '';
    });
    return flat;
  }), [filtered, columns]);

  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY_FORM); setAccLookup([]); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return; if (!selected) return; setForm({ ...selected }); setAccLookup([]); setModal('edit'); };
  const openDelete = () => { if (!vbf.can('BTNDELETE')) return; if (!selected) return; setModal('delete'); };

  const doAccountSearch = async q => {
    const j = await API(`/credits/account-search/?q=${encodeURIComponent(q||'')}`);
    if (j.ok) setAccLookup(j.rows);
  };

  const save = async () => {
    if (!form.title.trim()) { setError('عنوان الزامی است'); return; }
    // ترتیب کنترل‌ها با سرور یکی است
    if (!Number(form.source_type)) { setError('نوع منبع را مشخص کنید'); return; }
    if (Number(form.source_type) !== 3) {
      if (Number(form.other_source_type)) setForm(f => ({...f, other_source_type:0}));
    } else if (!Number(form.other_source_type)) { setError('نوع سایر منابع را مشخص کنید'); return; }
    if (!Number(form.credit_type)) { setError('نوع اعتبار را مشخص کنید'); return; }
    setSaving(true); setError('');
    const j = modal === 'new'
      ? await API('/credits/create/', { method:'POST', body: JSON.stringify(form) })
      : await API(`/credits/${selected.id}/update/`, { method:'PUT', body: JSON.stringify(form) });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/credits/${selected.id}/delete/`, { method:'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(); }
    else { setError(j.msg); setModal('delete_blocked'); }
  };

  const toolbar = (
    <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
      <div style={{display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap'}}>
        <div style={ui.toolBtns}>
          {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
          {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...ui.btnEdit, opacity: selected?1:0.4, cursor: selected?'pointer':'default'}}>✎ ویرایش</button>}
          {vbf.can('BTNDELETE') && <button onClick={openDelete} disabled={!selected} style={{...ui.btnDel, opacity: selected?1:0.4, cursor: selected?'pointer':'default'}}>✕ حذف</button>}
          <div style={{width:'1px',height:'26px',background:'#ccc',margin:'0 2px'}} />
          {vbf.can('BTNCREDITRECEIVE') && <button onClick={()=>{ if (selected) setModal('receive'); }} disabled={!selected} style={{...btnTool, opacity:selected?1:0.4}}>📥 دریافتی</button>}
          {vbf.can('BTNCHANGE') && <button onClick={()=>{ if (selected) setModal('change'); }} disabled={!selected} style={{...btnTool, background:'#1a237e', opacity:selected?1:0.4}}>🔄 انتقال</button>}
          <div style={{width:'1px',height:'26px',background:'#ccc',margin:'0 2px'}} />
          <ExcelModalButton title="منابع اعتباری" subtitle={`سال مالی ${toFarsi(year)}`} columns={excelCols} rows={excelRows} style={{padding:'5px 12px', fontSize:'11px'}} />
          <button onClick={()=>setModal('report')} style={{...btnTool, background:'#4a148c'}}>🖨️ چاپ/گزارش</button>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} style={{...ui.searchInput, flex:1, minWidth:'200px'}} placeholder="جستجو در عنوان یا کد..." />
        {loading && <span>⏳</span>}
        {filtered.length > 0 && <div style={ui.countBadge}>{toFarsi(filtered.length)} از {toFarsi(total||filtered.length)} منبع</div>}
      </div>
      <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
        <FCheck label="ارقام حسابداری" name="acc"    checked={showAcc}    onChange={e=>setShowAcc(e.target.checked)} />
        <FCheck label="ارقام بودجه"    name="budget" checked={showBudget} onChange={e=>setShowBudget(e.target.checked)} />
        <FCheck label="نمایش همه"      name="all"    checked={showAll}    onChange={e=>setShowAll(e.target.checked)} />
        <div style={{width:'1px', height:'26px', background:'#e0e0e0', margin:'0 4px'}} />
        <span style={{fontSize:'12px', color:'#555', fontWeight:'600'}}>بازه بر اساس:</span>
        <select value={by} onChange={e=>setBy(e.target.value)} style={miniSel}>
          <option value="date">تاریخ سند</option>
          <option value="no">شماره سند</option>
        </select>
        <input value={rangeFrom} onChange={e=>setRangeFrom(e.target.value)} onBlur={load} onKeyDown={e=>e.key==='Enter'&&load()} placeholder="از" dir="ltr" style={{...miniInput,width:'110px'}} />
        <input value={rangeTo} onChange={e=>setRangeTo(e.target.value)} onBlur={load} onKeyDown={e=>e.key==='Enter'&&load()} placeholder="تا" dir="ltr" style={{...miniInput,width:'110px'}} />
        {dateInfo.from && <span style={{fontSize:'11px', color:'#888'}}>سال {toFarsi(year)} — {toFarsi(dateInfo.from)} تا {toFarsi(dateInfo.to)}</span>}
      </div>
    </div>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="منابع اعتباری" subtitle="سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}>
        <table style={{...ui.table, tableLayout:'fixed'}}>
          <thead style={{position:'sticky',top:0,zIndex:10}}>
            <tr>
              {columns.map(c => (
                <th key={c.key} style={{...ui.th, width:wOf(c), minWidth:40, background:c.bg, color:c.tc, position:'relative', userSelect:'none'}}>
                  {c.label}
                  <span onMouseDown={e=>startResize(c.key, wOf(c), e)} style={{position:'absolute',left:0,top:0,bottom:0,width:'4px',cursor:'col-resize'}} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r,i) => {
              const sel = selected?.id === r.id;
              return (
                <tr key={r.id} style={{ background: sel ? '#1a2f50' : i%2===0 ? '#fff' : '#fdf6e3', cursor:'pointer' }}
                  onClick={() => setSelected(sel ? null : r)}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background='#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background=i%2===0?'#fff':'#fdf6e3'; }}>
                  {columns.map(c => {
                    const base = { ...ui.td };
                    if (c.right) base.textAlign='right';
                    if (c.mono)  { base.fontFamily='monospace'; base.fontWeight='700'; base.color = sel?'#C8A84B':'#1a237e'; }
                    if (c.num) {
                      const v = c.val(r);
                      return <td key={c.key} style={{...base, ...numCell, color: sel ? '#fff' : (c.strong && v>0 ? c.strong : '#444')}}>{numFmt(v)}</td>;
                    }
                    if (c.check) return <td key={c.key} style={{...base, padding:'6px 4px'}}>{c.check(r) ? <span style={{color:sel?'#81c784':'#1b5e20',fontWeight:'700'}}>✓</span> : ''}</td>;
                    const txt = c.render(r);
                    return <td key={c.key} style={{...base, color: sel?'#fff':'#1a1a2e', overflow:'hidden', textOverflow:'ellipsis'}} title={txt}>{txt}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && <div style={ui.empty}>📭 داده‌ای یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{...ui.modal, maxWidth:'680px'}} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ منبع اعتباری جدید' : `✎ ویرایش: ${selected?.title}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <FInput label="کد" name="code" value={form.code} onChange={e => setForm(f=>({...f,code:Number(e.target.value)}))} type="number" half />
                <FInput label="درصد استثناء" name="exception_pct" value={form.exception_pct} onChange={e => setForm(f=>({...f,exception_pct:Number(e.target.value)}))} type="number" half />
              </div>
              <div style={ui.row}><FInput label="عنوان" name="title" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} /></div>
              <div style={ui.row}><FInput label="عنوان اختصاری" name="brief_title" value={form.brief_title} onChange={e => setForm(f=>({...f,brief_title:e.target.value}))} /></div>
              <div style={ui.divider} />
              <div style={ui.row}>
                <FSelect label="نوع منبع *" name="source_type" value={form.source_type} onChange={e => setForm(f=>({...f,source_type:Number(e.target.value)}))} opts={SOURCE_TYPES} />
                <FSelect label="نوع سایر منابع" name="other_source_type" value={form.other_source_type} onChange={e => setForm(f=>({...f,other_source_type:Number(e.target.value)}))} opts={OTHER_SOURCES} />
              </div>
              <div style={ui.row}>
                <FSelect label="نوع اعتبار *" name="credit_type" value={form.credit_type} onChange={e => setForm(f=>({...f,credit_type:Number(e.target.value)}))} opts={CREDIT_TYPES} />
                <FSelect label="نوع حساب" name="accounting_kind" value={form.accounting_kind} onChange={e => setForm(f=>({...f,accounting_kind:Number(e.target.value)}))} opts={ACC_KINDS} />
              </div>
              <div style={ui.row}>
                <FSelect label="استانی/ملی" name="region_type" value={form.region_type} onChange={e => setForm(f=>({...f,region_type:Number(e.target.value)}))} opts={REGION_TYPES} />
                <div style={{display:'flex', flexDirection:'column', gap:'4px', flex:1, position:'relative'}}>
                  <label style={ui.mlabel}>حساب معین</label>
                  <input value={form.account_display} placeholder="کد یا عنوان حساب را تایپ کنید..."
                    onChange={e=>{ setForm(f=>({...f, account_display:e.target.value, account_code:0})); doAccountSearch(e.target.value); }}
                    onFocus={()=>doAccountSearch(form.account_display)} style={ui.input} />
                  {accLookup.length > 0 && (
                    <div style={dropdown}>
                      {accLookup.map(a => (
                        <div key={a.code} style={dropItem}
                          onClick={()=>{ setForm(f=>({...f, account_code:a.code, account_display:`${a.account_no} - ${a.title}`})); setAccLookup([]); }}>
                          {a.account_no} — {a.title}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={ui.divider} />
              <div style={{background:'#f8f9fa', borderRadius:'10px', padding:'14px 16px', border:'1px solid #e8eaf6'}}>
                <div style={{fontSize:'12px', fontWeight:'700', color:'#1a237e', marginBottom:'10px'}}>🔐 الزامات ثبت سند هزینه</div>
                <div style={{display:'flex', flexWrap:'wrap', gap:'10px'}}>
                  <FCheck label="الزام مرکز هزینه در سند هزینه" name="cost_center" checked={form.cost_center} onChange={e => setForm(f=>({...f,cost_center:e.target.checked}))} />
                  <FCheck label="الزام طرح/برنامه در سند هزینه" name="program" checked={form.program} onChange={e => setForm(f=>({...f,program:e.target.checked}))} />
                  <FCheck label="الزام پروژه/فعالیت در سند هزینه" name="project" checked={form.project} onChange={e => setForm(f=>({...f,project:e.target.checked}))} />
                  <FCheck label="الزام کد هزینه در سند هزینه" name="hazineh" checked={form.hazineh} onChange={e => setForm(f=>({...f,hazineh:e.target.checked}))} />
                  <FCheck label="وابسته به سال مالی" name="depends_year" checked={form.depends_year} onChange={e => setForm(f=>({...f,depends_year:e.target.checked}))} />
                </div>
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ در حال ذخیره...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{...ui.modal, maxWidth:'460px'}} onClick={e => e.stopPropagation()}>
            <div style={{...ui.mHeader, background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{...ui.mBody, textAlign:'center', padding:'28px'}}>
              <div style={{fontSize:'36px',marginBottom:'12px'}}>🗑️</div>
              <div style={{fontSize:'15px',fontWeight:'700',color:'#1a2f50',marginBottom:'8px'}}>آیا مطمئن هستید؟</div>
              <div style={{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',marginTop:'10px',fontWeight:'700',color:'#b71c1c'}}>
                [{selected?.code}] {selected?.title}
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر، انصراف</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{...ui.btnSave, background:'#b71c1c'}}>{saving ? '⏳...' : 'بله، حذف شود'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete_blocked' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{...ui.modal, maxWidth:'420px'}} onClick={e => e.stopPropagation()}>
            <div style={{...ui.mHeader, background:'linear-gradient(135deg,#e65100,#f57c00)'}}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{...ui.mBody, textAlign:'center', padding:'28px', fontSize:'14px',color:'#1a2f50',fontWeight:'700',lineHeight:'1.8'}}>این رکورد در جام ارجان مورد استفاده قرار گرفته<br />و قابل حذف نیست</div>
            <div style={ui.mFooter}><button onClick={() => { setModal(null); setError(''); }} style={ui.btnSave}>باشه</button></div>
          </div>
        </div>
      )}

      {modal === 'receive' && selected && (
        <CreditReceiveModal
          creditId={selected.id} year={year}
          onClose={()=>setModal(null)}
          onChanged={load}
        />
      )}

      {modal === 'change' && selected && (
        <CreditChangeModal
          creditId={selected.id}
          creditTitle={`${selected.code} - ${selected.title}`}
          year={year}
          onClose={()=>setModal(null)}
          onChanged={()=>{ setSelected(null); load(); }}
        />
      )}

      {modal === 'report' && (
        <CreditReportModal
          data={filtered}
          selected={selected}
          year={year}
          dateInfo={dateInfo}
          by={by}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          onClose={()=>setModal(null)}
        />
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}

// استایل‌های مخصوص این صفحه
const numCell   = { direction:'ltr', textAlign:'left', fontWeight:'600', fontFamily:"'Vazirmatn',Tahoma,sans-serif" };
const miniSel   = { padding:'6px 10px', border:'1.5px solid #e0e0e0', borderRadius:'7px', fontSize:'12px', fontFamily:'inherit', background:'#fff', outline:'none' };
const miniInput = { padding:'6px 10px', border:'1.5px solid #e0e0e0', borderRadius:'7px', fontSize:'12px', fontFamily:'inherit', outline:'none', textAlign:'center' };
const dropdown  = { position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #ddd', borderRadius:'8px', maxHeight:'190px', overflowY:'auto', zIndex:60, boxShadow:'0 6px 20px rgba(0,0,0,0.18)' };
const dropItem  = { padding:'7px 11px', fontSize:'12px', cursor:'pointer', borderBottom:'1px solid #f5f5f5', textAlign:'right' };
const btnTool   = { padding:'6px 14px', border:'none', borderRadius:'7px', fontSize:'12px', fontWeight:'600', color:'#fff', background:'#00695c', cursor:'pointer', fontFamily:'inherit' };
